import React, { useEffect, useState } from 'react';
import { produce } from 'immer';
import { useLocation } from 'react-router-dom';
import Modal from '../../components/organisms/modal';
import {
  GetChatThreadsParams,
  GetChatThreadsResponse,
  IChatThread,
  MarkThreadAsRead,
  MarkThreadAsReadResponse,
  useGetChatThreads
} from '../../libs/chat';
import { useChatSocket, EventNames } from '../../contexts/chats/socket';
import { usePatientGroup } from '../../contexts/patient-group';
import { useMetaData } from '../../contexts/metadata';
import {
  determineMissingChatThreads,
  getAndAddMissingThreads
} from '../../contexts/triage/utils';
import { PostJSON } from '../../libs/request';

const PAGE_LIMIT = 50;

export interface ChatMessage {
  id: number | string;
  datetime: string;
  text?: string;
  isSecure?: boolean;
  isRead: boolean;
  from?: string;
  to?: string;
  createdBy?: string;
  type: string;
}

export interface ChatsValue {
  isLoading: boolean;
  threads: IChatThread[];
  total: number;
  isSecure: boolean;
  findThreadByID: (patientId: IChatThread['patientId']) => IChatThread;
  findThreadsByKeyword: (keyword: string, page?: number) => Promise<number>;
  updateThread: (thread: IChatThread) => void;
  updateThreadAsRead: (threadId: IChatThread['id']) => void;
  updateBrand: (
    patientId: IChatThread['patientId'],
    brand: IChatThread['brand']
  ) => void;
  startChat: (thread: IChatThread) => void;
  addThreadToContext: (threads: IChatThread[]) => void;
  setTriagePatientIds: (patientIds: IChatThread['patientId'][]) => void;
  getThreadByPatientIds: (
    patientIds: IChatThread['patientId'][]
  ) => Promise<GetChatThreadsResponse>;
}

const ChatsContext = React.createContext<ChatsValue>({
  isLoading: true,
  threads: [],
  total: 0,
  isSecure: false,
  findThreadByID: null,
  findThreadsByKeyword: null,
  updateThread: null,
  updateThreadAsRead: null,
  updateBrand: null,
  startChat: null,
  addThreadToContext: null,
  setTriagePatientIds: null,
  getThreadByPatientIds: null
});

export const ChatsProvider: React.FC = (props) => {
  const [triagePatientIds, setTriagePatientIds] = useState([]);
  const location = useLocation();
  const ptTest = '/chat/pt/';
  const isPatientFacing = location.pathname.slice(0, ptTest.length) === ptTest;
  // Fetch the current patient group
  const { isLoading: pgLoading, getActiveGroup } = usePatientGroup();
  // Use undefined here as a way to differentiate between
  // null, which is the 'All Patients View' and undefined,
  // which is 'not done fetching yet'
  const activePatientGroup = !pgLoading ? getActiveGroup() : undefined;
  const { account, settings, isLoading: metaDataLoading } = useMetaData();
  let isDefaultSecure = false;
  if (isPatientFacing) {
    isDefaultSecure = true;
  } else if (settings?.chatOptions?.isDefaultSecure) {
    isDefaultSecure = settings.chatOptions.isDefaultSecure;
  }
  let isChatEnabled = false;
  if (account?.isChatEnabled) {
    isChatEnabled = account.isChatEnabled;
  }
  const shouldFetchInitialChats =
    isChatEnabled && !pgLoading && !metaDataLoading;
  const {
    threads,
    total,
    setThreads,
    isLoading: chatsLoading
  } = useGetChatThreads(
    {
      page: 0,
      limit: PAGE_LIMIT,
      filters: activePatientGroup?.filters
    },
    shouldFetchInitialChats
  );
  const socket = useChatSocket();
  const socketId = socket?.id;
  useEffect(() => {
    if (socket && threads && threads.length > 0) {
      const patientIds = threads.map((thread) => thread.patientId);
      socket.on(EventNames.NEW_UNREAD_FOR_PATIENT, (patientId, thread) => {
        let isOptedOut = false;
        if (thread && thread.isOptedOut) {
          isOptedOut = thread.isOptedOut;
        }
        /**
         * Only do this work if the patient id is actually
         * part of the current patient view's patient ids
         */
        if (triagePatientIds && triagePatientIds.includes(patientId)) {
          const result = determineMissingChatThreads([patientId], threads);
          getAndAddMissingThreads(result, addThreadToContext);
          incrementUnread(patientId, isOptedOut);
        } else if (patientIds.includes(patientId)) {
          /**
           * Otherwise, if we already have this patient's
           * chat in state, increment the unread number
           * for this patient
           */
          incrementUnread(patientId, isOptedOut);
        }
      });
      socket.on(EventNames.THREAD_READ, (threadId, readAt) => {
        clearUnreadForThread(threadId, readAt);
      });
    }
    return () => {
      if (socket) {
        socket.off(EventNames.NEW_UNREAD_FOR_PATIENT);
        socket.off(EventNames.THREAD_READ);
      }
    };
  }, [socketId, threads, triagePatientIds]);

  /**
   * We separate this functionality from the useEffect
   * above so that the emit only happens twice, instead
   * of happening three times.
   */
  useEffect(() => {
    if (socket && threads && threads.length > 0) {
      /**
       * Ensure that the user is added to all the patient threads
       * for the patient view they're looking at
       */
      const patientIds = threads.map((thread) => thread.patientId);
      socket.emit(EventNames.ENTER_USER, patientIds);
    }
    return () => {
      if (socket) {
        /**
         * Since ENTER_USER takes care of the user's socket
         * leaving all existing rooms prior to joining new
         * rooms, we don't need to worry about emitting
         * a LEAVE_USER event here.
         */
      }
    };
  }, [socketId, threads]);

  const findThreadByID = (id: IChatThread['id']) => {
    const index = threads.findIndex((thread) => thread.id === id);
    return threads[index];
  };

  const findThreadsByKeyword = (
    keyword: string,
    page?: number
  ): Promise<number> => {
    return new Promise((resolve, reject) => {
      let total: number;

      /**
       * When making use of a keyword, we initially
       * set it to undefined to indicate the component
       * has loaded but no change has happened to the
       * keyword. Doing this prevents multiple, unnecessary
       * calls to /chat/data_threads/.
       */
      if (keyword === undefined) {
        return;
      }

      const params: GetChatThreadsParams = {
        name: keyword === null || keyword.length === 0 ? undefined : keyword,
        page: page - 1 || 0,
        limit: PAGE_LIMIT,
        filters: activePatientGroup?.filters
      };
      // Make call to database to paginate and add to context
      PostJSON({
        url: '/chat/data_threads/',
        params
      })
        .then((res: GetChatThreadsResponse) => {
          total = res.total;
          const nextThreads = res.threads;
          setThreads(nextThreads);
          resolve(total);
        })
        .catch(reject);
    });
  };

  const getThreadByPatientIds = (
    patientIds: number[]
  ): Promise<GetChatThreadsResponse> => {
    return new Promise((resolve, reject) => {
      if (!patientIds) {
        return;
      }
      const params: GetChatThreadsParams = {
        patientIds,
        limit: PAGE_LIMIT,
        filters: activePatientGroup?.filters
      };
      // Make call to database to paginate and add to context
      PostJSON({
        url: '/chat/data_threads/',
        params
      })
        .then((res: GetChatThreadsResponse) => {
          resolve(res);
        })
        .catch(reject);
    });
  };

  const updateThread = (updatedThread: IChatThread) => {
    const updatedThreads = produce(threads, (draft) => {
      const threadIndex = draft.findIndex(
        (thread) => thread.id === updatedThread.id
      );
      if (threadIndex !== -1) {
        draft[threadIndex] = updatedThread;
      } else {
        draft.unshift(updatedThread); // In case it's not already present
      }
    });
    setThreads(updatedThreads);
  };

  const updateThreadAsRead = (threadId: IChatThread['id']) => {
    const thread = findThreadByID(threadId);
    if (!thread) {
      return;
    }
    MarkThreadAsRead({
      patientId: thread.patientId,
      brandId: thread.brandId
    }).then((response) => {
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Updating Thread',
          content: response.message
        });
        return;
      }
      const thread = (response as MarkThreadAsReadResponse).thread;
      updateThread(thread);
    });
  };

  const startChat = (thread: IChatThread) => {
    const updatedThreads = produce(threads, (draft) => {
      const threadIndex = draft.findIndex(
        (_thread) => _thread.id === thread.id
      );
      if (threadIndex !== -1) {
        draft.splice(threadIndex, 1);
      }
      draft.splice(0, 0, thread);
    });
    setThreads(updatedThreads);
  };

  const addThreadToContext = (missingThreads: IChatThread[]) => {
    const updatedThreads = produce(threads, (draft) => {
      missingThreads.forEach((thread) => {
        draft.push(thread);
      });
    });
    setThreads(updatedThreads);
  };

  const incrementUnread = (patientId: number, isOptedOut: boolean) => {
    const updatedThreads = produce(threads, (draft) => {
      const threadIndex = draft.findIndex(
        (thread) => thread.patientId === patientId
      );
      if (threadIndex !== -1) {
        draft[threadIndex].numUnread = draft[threadIndex].numUnread + 1;
        draft[threadIndex].isOptedOut = isOptedOut;
      }
    });
    setThreads(updatedThreads);
  };

  const clearUnreadForThread = (threadId: number, readAt: Date) => {
    const updatedThreads = produce(threads, (draft) => {
      const threadIndex = draft.findIndex((thread) => thread.id === threadId);
      if (threadIndex !== -1) {
        draft[threadIndex].numUnread = 0;
        if (draft[threadIndex].lastCMMessage) {
          draft[threadIndex].lastCMMessage.isRead = true;
          draft[threadIndex].lastCMMessage.readAt = readAt;
        }
      }
    });
    setThreads(updatedThreads);
  };

  const updateBrand = (patientId: number, brand: IChatThread['brand']) => {
    const updatedThreads = produce(threads, (draft) => {
      const threadIndex = draft.findIndex(
        (thread) => thread.patientId === patientId
      );
      if (threadIndex !== -1) {
        draft[threadIndex].brand = brand;
        draft[threadIndex].brandId = brand.id;
      }
    });
    setThreads(updatedThreads);
  };

  const value: ChatsValue = {
    isLoading:
      pgLoading === true || metaDataLoading === true || chatsLoading === true,
    threads,
    total,
    isSecure: isDefaultSecure,
    findThreadByID,
    findThreadsByKeyword,
    updateThread,
    updateThreadAsRead,
    updateBrand,
    startChat,
    addThreadToContext,
    setTriagePatientIds,
    getThreadByPatientIds
  };

  return <ChatsContext.Provider value={value} {...props} />;
};

export function useChats(): ChatsValue {
  const context = React.useContext(ChatsContext);
  if (context === undefined) {
    throw new Error('useChats must be used within a ChatsProvider');
  }
  return context;
}
